// src/App.js
import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import Navigation from './components/Navigation';
import Footer from './components/Footer';
import IdeaCapture from './components/IdeaCapture';
import IdeaList from './components/IdeaList';
import TagList from './components/TagList';
import Auth from './components/Auth';
import OAuthCallback from './components/OAuthCallback';
import UserProfile from './components/UserProfile';
import UploadAvatar from './components/UploadAvatar';
import Privacy from './components/Privacy';
import Terms from './components/Terms';
import Support from './components/Support';
import ResetPassword from './components/ResetPassword';
import MobilePasswordReset from './components/MobilePasswordReset';
import HowItWorks from './components/HowItWorks';
import AITools from './components/AITools';
import AuthBridge from './components/AuthBridge';
import ProtectedRoute from './components/ProtectedRoute';
import OperatorRoute from './components/OperatorRoute';
import AdminEntitlements from './components/AdminEntitlements';
import ErrorBoundary from './components/ErrorBoundary';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth } from './firebase';
import { useMobileAuth } from './hooks/useMobileAuth';
import { EntitlementSummaryProvider } from './context/EntitlementSummaryContext';

function App() {
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [darkMode, setDarkMode] = useState(() => {
    const savedPreference = localStorage.getItem('darkMode');
    return savedPreference ? JSON.parse(savedPreference) : false;
  });

  useAuthState(auth);

  // Apply dark mode class to document root
  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    localStorage.setItem('darkMode', JSON.stringify(darkMode));
  }, [darkMode]);

  return (
    <ErrorBoundary>
      <HelmetProvider>
        <Router>
          <EntitlementSummaryProvider>
            <AppContent 
              hasUnsavedChanges={hasUnsavedChanges}
              setHasUnsavedChanges={setHasUnsavedChanges}
              darkMode={darkMode} 
              setDarkMode={setDarkMode}
            />
          </EntitlementSummaryProvider>
        </Router>
      </HelmetProvider>
    </ErrorBoundary>
  );
}

function AppContent({ hasUnsavedChanges, setHasUnsavedChanges, darkMode, setDarkMode }) {
  const location = useLocation();
  const isAuthPage = location.pathname === '/auth';
  const [billingNotice, setBillingNotice] = useState(null);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('billing') === 'success') {
      setBillingNotice(
        'Thanks for subscribing! If your paid credits do not appear yet, refresh the page in a moment.'
      );
      params.delete('billing');
      const nextSearch = params.toString();
      window.history.replaceState(
        {},
        '',
        `${location.pathname}${nextSearch ? `?${nextSearch}` : ''}`
      );
    } else if (params.get('billing') === 'cancel') {
      setBillingNotice('Checkout was canceled. You can subscribe anytime from the Support page or Path Selector.');
      params.delete('billing');
      const nextSearch = params.toString();
      window.history.replaceState(
        {},
        '',
        `${location.pathname}${nextSearch ? `?${nextSearch}` : ''}`
      );
    }
  }, [location.pathname, location.search]);
  
  // Handle mobile-to-web authentication
  useMobileAuth();

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex flex-col">
      {!isAuthPage && (
        <Navigation 
          hasUnsavedChanges={hasUnsavedChanges} 
          darkMode={darkMode} 
          setDarkMode={setDarkMode} 
        />
      )}
      {isAuthPage || location.pathname === '/oauth-callback' || location.pathname === '/auth-bridge' || location.pathname === '/auth/mobile-reset' || (location.pathname === '/auth' && location.search.includes('mode=resetPassword')) ? (
        <Routes>
          <Route path="/auth/mobile-reset" element={<MobilePasswordReset />} />
          <Route path="/auth" element={location.search.includes('mode=resetPassword') ? <ResetPassword /> : <Auth />} />
          <Route path="/oauth-callback" element={<OAuthCallback />} />
          <Route path="/auth-bridge" element={<AuthBridge />} />
        </Routes>
      ) : (
        <>
          <main className="max-w-7xl mx-auto px-2 md:px-4 py-4 md:py-8 flex-1">
            {billingNotice && (
              <div className="mb-4 p-4 rounded-xl border border-purple-200 dark:border-purple-800 bg-purple-50 dark:bg-purple-900/20 text-sm text-purple-900 dark:text-purple-100 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                <p>{billingNotice}</p>
                <button
                  type="button"
                  onClick={() => setBillingNotice(null)}
                  className="text-purple-800 dark:text-purple-200 underline font-medium shrink-0"
                >
                  Dismiss
                </button>
              </div>
            )}
            <Routes>
              <Route path="/" element={<ProtectedRoute><IdeaList setHasUnsavedChanges={setHasUnsavedChanges} darkMode={darkMode} /></ProtectedRoute>} />
              <Route path="/add-idea" element={<ProtectedRoute><IdeaCapture setHasUnsavedChanges={setHasUnsavedChanges} /></ProtectedRoute>} />
              <Route path="/tags" element={<ProtectedRoute><TagList /></ProtectedRoute>} />
              <Route path="/ideas" element={<ProtectedRoute><IdeaList setHasUnsavedChanges={setHasUnsavedChanges} darkMode={darkMode} /></ProtectedRoute>} />
              <Route path="/upload-avatar" element={<ProtectedRoute><UploadAvatar /></ProtectedRoute>} />
              <Route path="/profile" element={<ProtectedRoute><UserProfile /></ProtectedRoute>} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/support" element={<Support />} />
              <Route path="/how-it-works" element={<HowItWorks />} />
              <Route path="/ai-tools" element={<AITools />} />
              <Route path="/admin/entitlements" element={<OperatorRoute><AdminEntitlements /></OperatorRoute>} />
            </Routes>
          </main>
          {!isAuthPage && <Footer />}
        </>
      )}
    </div>
  );
}

export default App;